export type PasswordStrength = 'weak' | 'medium' | 'strong' | 'veryStrong';

export interface PasswordStrengthResult {
  strength: PasswordStrength;
  score: number; // 0-4
}

/**
 * Calculates password strength based on length and character variety
 * @param password - The password to evaluate
 * @returns Password strength result with strength level and score
 */
export function calculatePasswordStrength(
  password: string,
): PasswordStrengthResult {
  if (!password) {
    return { strength: 'weak', score: 0 };
  }

  let score = 0;

  // Length checks
  if (password.length >= 6) score += 1; // Minimum length
  if (password.length >= 9) score += 1; // Good length
  if (password.length >= 12) score += 1; // Very good length

  // Character variety checks
  const hasLowerCase = /[a-z]/.test(password);
  const hasUpperCase = /[A-Z]/.test(password);
  const hasNumbers = /\d/.test(password);
  const hasSpecialChars = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);

  // Variety bonus
  const varietyCount = [
    hasLowerCase,
    hasUpperCase,
    hasNumbers,
    hasSpecialChars,
  ].filter(Boolean).length;

  if (varietyCount >= 2) score += 1; // At least 2 character types
  if (varietyCount >= 3 && password.length >= 9) score += 1; // 3+ types with good length
  if (varietyCount >= 4 && password.length >= 12) score += 1; // All types with very good length

  // Cap score at 4
  score = Math.min(score, 4);

  // Determine strength level
  let strength: PasswordStrength;
  if (score <= 1) {
    strength = 'weak';
  } else if (score === 2) {
    strength = 'medium';
  } else if (score === 3) {
    strength = 'strong';
  } else {
    strength = 'veryStrong';
  }

  return { strength, score };
}

/**
 * Gets the color class for password strength indicator
 */
export function getPasswordStrengthColor(
  strength: PasswordStrength,
): string {
  switch (strength) {
    case 'weak':
      return 'bg-red-500';
    case 'medium':
      return 'bg-yellow-500';
    case 'strong':
      return 'bg-blue-500';
    case 'veryStrong':
      return 'bg-green-500';
    default:
      return 'bg-gray-500';
  }
}
